import type { AxiosRequestConfig } from 'axios'
import { getActivePinia } from 'pinia'
import { api } from './client'
import { getCsrfToken } from './utils'
import {
  useWorkspaceStore,
  getStoredWorkspaceId,
  clearStoredWorkspaceId,
} from '@/store/useWorkspaceStore'

let isRedirecting = false

/** Message returned by the SetCurrentWorkspace middleware on a membership rejection. */
const WORKSPACE_FORBIDDEN_MESSAGE = 'You do not have access to this workspace.'

function currentWorkspaceId(): string | null {
  // Pinia may not be installed yet (e.g. requests fired during module init).
  if (getActivePinia()) {
    return useWorkspaceStore().currentWorkspaceId
  }
  return getStoredWorkspaceId()
}

api.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method ?? '')) {
    const csrf = getCsrfToken()
    if (csrf) config.headers['X-XSRF-TOKEN'] = csrf
  }

  const workspaceId = currentWorkspaceId()
  if (workspaceId !== null && !config.headers.has('X-Workspace-Id')) {
    config.headers['X-Workspace-Id'] = workspaceId
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 419 = stale CSRF token — re-fetch and retry once
    if (
      error.response?.status === 419 &&
      !(error.config as AxiosRequestConfig & { _csrfRetried?: boolean })._csrfRetried
    ) {
      try {
        await api.get('/sanctum/csrf-cookie')
        ;(error.config as AxiosRequestConfig & { _csrfRetried?: boolean })._csrfRetried = true
        return api(error.config)
      } catch {
        // fall through
      }
    }

    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true
      localStorage.removeItem('user')
      window.location.href = '/login'
      setTimeout(() => { isRedirecting = false }, 3000)
    }

    // 403 from the workspace middleware = the selected workspace is no longer
    // one of the user's memberships. Clear the selection and re-select.
    if (
      error.response?.status === 403 &&
      error.response?.data?.message === WORKSPACE_FORBIDDEN_MESSAGE &&
      !isRedirecting
    ) {
      isRedirecting = true
      if (getActivePinia()) useWorkspaceStore().clearCurrent()
      else clearStoredWorkspaceId()
      window.location.assign('/workspaces/select')
      setTimeout(() => { isRedirecting = false }, 3000)
    }

    return Promise.reject(error)
  },
)

export default api
